import { AfterViewChecked, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription, forkJoin } from 'rxjs';
import { GENDER } from 'src/app/constants/health-history.constants';
import { MASK_CONSTANTS } from 'src/app/constants/mask.constants';
import { SpinnerComponent } from 'src/app/modules/shared/spinner/spinner.component';
import { ValidationUtilityService } from 'src/app/modules/shared/utils/validation.util/validation-utility.service';
import { DataCarrierService } from 'src/app/services/core/data-carrier.service';
import { GeneralInfoService } from 'src/app/services/ha-new/general-info.service';
import { LookupService } from 'src/app/services/ha-new/lookup.service';
import { NewHAService } from 'src/app/services/ha-new/new-ha.service';

/**
 * HA Personal Info, Emergency Contacts and Living Will
 */
@Component({
	selector: 'app-personal-info',
	templateUrl: './personal-info.component.html',
	styleUrls: ['./personal-info.component.scss'],
})
export class PersonalInfoComponent implements OnInit, AfterViewChecked {
	/** Holds personal info form */
	personalInfoForm: FormGroup;
	/** Holds emergency contact info form */
	emergencyContactForm: FormGroup;
	/** holds ethnic background data */
	ethnicBackgroundData: any;
	/** holds education lookup data */
	educationLookupData: any;
	/** holds marital status lookup data */
	maritalStatusLookupData: any;
	/** holds personal information bean */
	personalInfo: any;
	/** masks phone number in format (123) 456-7890 */
	phoneMask = MASK_CONSTANTS.CELL_PHONE;
	/** holds living will form info */
	livingWillForm: FormGroup;
	/** Data carrier subscriptions */
	subscriptions: Subscription[] = [];
	/** holds ha id */
	healthAssessmentID: number = 0;
	/** holds appointment id */
	apptID: any;
	/** holds readOnly flag */
	readOnly: boolean = false;
	scrollTo = '';
	gender = GENDER;
	returningUser: boolean = false;
	/** holds identity lookup data */
	genderIdentityLookup: any;
	preferredTitleLookup: any;
	preferredPronounsLookup: any;

	/**
	 * Constructor
	 * @param lookupService
	 * @param generalInfoService
	 */
	constructor(
		private lookupService: LookupService,
		private generalInfoService: GeneralInfoService,
		private spinner: SpinnerComponent,
		private dataCarrierService: DataCarrierService,
		private activatedRoute: ActivatedRoute,
		private haService: NewHAService
	) {
		this.activatedRoute.queryParams.subscribe(params => {
			//console.log('params:', params);
			this.apptID = params.apptID;
			this.readOnly = params.readOnly === 'true';
			this.healthAssessmentID = params.healthAssessmentID;
			this.returningUser = params.returningUser === 'true';
			if (this.scrollTo != 'false') {
				this.scrollTo = params.section;
			}
		});
	}

	/**
	 * on component initialization
	 */
	ngOnInit() {
		// TODO workphone and gender integration on this component.
		this.initializePersonalInfoForm();
		this.initializeEmergencyContactInfoForm();
		this.initializeLivingWillForm();
		this.lookup();
		// If there is no appt id and health assessment id - service returns 400 and so not making calls
		if (this.apptID && this.healthAssessmentID) {
			this.presetFormFields();
		}
	}

	ngAfterViewChecked() {
		if (this.scrollTo != 'false') {
			if (this.haService.scrollView(this.scrollTo)) {
				this.scrollTo = 'false';
			}
		}
	}

	/**
	 * Initializes personal info form
	 */
	initializePersonalInfoForm() {
		this.personalInfoForm = new FormGroup({
			homePhone: new FormControl('', Validators.compose([ValidationUtilityService.haOptionalPhoneNumberValidator])),
			cellPhone: new FormControl('', Validators.compose([ValidationUtilityService.haOptionalPhoneNumberValidator])),
			preferredEmail: new FormControl('', Validators.compose([ValidationUtilityService.HaOptionalEmailValidator])),
			ethnicBackgroundID: new FormControl({
				value: '',
				disabled: this.readOnly,
			}),
			maritalStatusID: new FormControl({ value: '', disabled: this.readOnly }),
			educationID: new FormControl({ value: '', disabled: this.readOnly }),
			genderIdentityID: new FormControl({ value: '', disabled: this.readOnly }),
			preferredTitleID: new FormControl({ value: '', disabled: this.readOnly }),
			preferredPronounsID: new FormControl({
				value: '',
				disabled: this.readOnly,
			}),
		});
	}

	/**
	 * initializes emergency contact info form
	 */
	initializeEmergencyContactInfoForm() {
		this.emergencyContactForm = new FormGroup({
			phone: new FormControl('', Validators.compose([ValidationUtilityService.haOptionalPhoneNumberValidator])),
			relationship: new FormControl(''),
			fullName: new FormControl(''),
		});
	}

	/**
	 * initializes living will form
	 */
	initializeLivingWillForm() {
		this.livingWillForm = new FormGroup({
			haveLivingWill: new FormControl({ value: '', disabled: this.readOnly }),
			discussOption: new FormControl({ value: '', disabled: this.readOnly }),
		});
	}

	/**
	 * Loads lookup values for dropdown
	 */
	lookup() {
		this.LoadEthnicBackgroundLookupValues();
		this.LoadMaritalStatusLookupValues();
		this.LoadEducationLevelLookupValues();
		this.getIdentityLookupValues();
	}

	/**
	 * Loads ethnic background lookup
	 */
	LoadEthnicBackgroundLookupValues() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.lookupService.getEthnicBackgroundLookupValues().subscribe(
				response => {
					this.ethnicBackgroundData = response.data;
				},
				error => {
					console.log(error);
				}
			)
		);
	}

	/**
	 * Loads marital status lookup
	 */
	LoadMaritalStatusLookupValues() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.lookupService.getMaritalStatusLookupValues().subscribe(
				response => {
					this.spinner.stopLoader();
					this.maritalStatusLookupData = response.data;
				},
				error => {
					this.spinner.stopLoader();
					console.log(error);
				}
			)
		);
	}

	/**
	 * Loads education level lookup
	 */
	LoadEducationLevelLookupValues() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.lookupService.getEducationLevelLookupValues().subscribe(
				response => {
					this.spinner.stopLoader();
					this.educationLookupData = response.data;
				},
				error => {
					this.spinner.stopLoader();
					console.log(error);
				}
			)
		);
	}

	/**
	 * Presets forms with data from service
	 */
	presetFormFields() {
		this.getPersonalInfo(), this.getEmergencyContactInfo(), this.getLivingWill();
	}

	/**
	 * preset personal info related data
	 */
	getPersonalInfo() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.generalInfoService.getPersonalInfo(this.apptID, this.healthAssessmentID).subscribe(
				response => {
					this.spinner.stopLoader();
					this.personalInfo = response.data;
					const profileRelatedInfo = this.dataCarrierService.getChannel().getValue().profile;
					//console.log(profileRelatedInfo)
					if (profileRelatedInfo && profileRelatedInfo.profileInfo) {
						this.personalInfo['firstName'] = profileRelatedInfo.profileInfo.firstName;
						this.personalInfo['lastName'] = profileRelatedInfo.profileInfo.lastName;
						this.personalInfo['dateOfBirth'] = profileRelatedInfo.profileInfo.dateOfBirth;
						this.personalInfo['gender'] = profileRelatedInfo.profileInfo.gender;
					}
					this.personalInfoForm.controls.homePhone.setValue(this.personalInfo.homePhone);
					this.personalInfoForm.controls.cellPhone.setValue(this.personalInfo.cellPhone);
					this.personalInfoForm.controls.preferredEmail.setValue(this.personalInfo.preferredEmail);
					this.personalInfoForm.controls.ethnicBackgroundID.setValue(this.personalInfo.ethnicBackgroundID);
					this.personalInfoForm.controls.maritalStatusID.setValue(this.personalInfo.maritalStatusID);
					this.personalInfoForm.controls.educationID.setValue(this.personalInfo.educationID);
					this.personalInfoForm.controls.preferredTitleID.setValue(this.personalInfo.preferredTitleID);
					this.personalInfoForm.controls.preferredPronounsID.setValue(this.personalInfo.preferredPronounsID);
					if (this.personalInfo.genderIdentityID == null) {
						const gender = this.personalInfo.gender;
						switch (gender) {
							case 'MALE':
								this.personalInfoForm.controls.genderIdentityID.setValue(1);
								break;
							case 'FEMALE':
								this.personalInfoForm.controls.genderIdentityID.setValue(2);
								break;
							case 'UNKNOWN':
								this.personalInfoForm.controls.genderIdentityID.setValue(null);
								this.personalInfoForm.controls.genderIdentityID.setValidators([Validators.required]);
								this.personalInfoForm.controls.genderIdentityID.updateValueAndValidity();
								break;
						}
					} else {
						this.personalInfoForm.controls.genderIdentityID.setValue(this.personalInfo.genderIdentityID);
					}
				},
				err => {
					this.spinner.stopLoader();
					console.log(err);
				}
			)
		);
	}

	/**
	 * Presets emergency contact info
	 */
	getEmergencyContactInfo() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.generalInfoService.getEmergencyContactInfo(this.apptID, this.healthAssessmentID).subscribe(
				response => {
					this.spinner.stopLoader();
					this.emergencyContactForm.controls.phone.setValue(response.data.phone);
					this.emergencyContactForm.controls.relationship.setValue(response.data.relationship);
					this.emergencyContactForm.controls.fullName.setValue(response.data.fullName);
				},
				err => {
					this.spinner.stopLoader();
					console.log(err);
				}
			)
		);
	}

	/**
	 * presets living will info
	 */
	getLivingWill() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.generalInfoService.getLivingWillInfo(this.apptID, this.healthAssessmentID).subscribe(
				response => {
					this.spinner.stopLoader();
					this.livingWillForm.controls.haveLivingWill.setValue(response.data.haveLivingWill);
					this.livingWillForm.controls.discussOption.setValue(response.data.discussOption);
				},
				err => {
					this.spinner.stopLoader();
					console.log(err);
				}
			)
		);
	}

	/**
	 * Saves form
	 */
	next() {
		if (!this.readOnly) {
			if (this.personalInfoForm.valid && this.emergencyContactForm.valid && this.livingWillForm.valid) {
				this.savePersonalInfo();
				this.saveEmergencyContactInfo();
				this.saveLivingWillInfo();
				this.haService.redirect(
					['primary-care'],
					[],
					[],
					this.apptID,
					this.healthAssessmentID,
					this.readOnly,
					'general-primarycare',
					this.returningUser
				);
			} else {
				this.scrollToInvalidForm();
				this.personalInfoForm.get('genderIdentityID').markAsTouched();
			}
		}
	}

	/**
	 * scrolls first invalid form into view
	 */
	scrollToInvalidForm() {
		const firstInvalidElement = document.querySelector('.ng-invalid[formControlName]') as HTMLElement;
		this.haService.scroll(firstInvalidElement);
	}

	/**
	 * saves personal information
	 */
	savePersonalInfo() {
		const personalInfoObj = { ...this.personalInfoForm.getRawValue() };
		personalInfoObj['address'] = this.personalInfo.address;
		personalInfoObj['healthAssessmentID'] = this.healthAssessmentID;
		this.subscriptions.push(
			this.generalInfoService.savePersonalInfo(this.apptID, personalInfoObj).subscribe(
				res => {
					console.log(res);
				},
				err => {
					console.log(err);
				}
			)
		);
	}

	/**
	 * saves emergency contact information
	 */
	saveEmergencyContactInfo() {
		this.subscriptions.push(
			this.generalInfoService
				.saveEmergencyContactInfo(this.apptID, {
					...this.emergencyContactForm.getRawValue(),
					healthAssessmentID: this.personalInfo.healthAssessmentID,
				})
				.subscribe(
					res => {
						//console.log(res);
					},
					err => {
						console.log(err);
					}
				)
		);
	}

	/**
	 * saves living will information
	 */
	saveLivingWillInfo() {
		this.subscriptions.push(
			this.generalInfoService
				.saveLivingWillInfo(this.apptID, {
					...this.livingWillForm.getRawValue(),
					healthAssessmentID: this.personalInfo.healthAssessmentID,
				})
				.subscribe(
					res => {
						//console.log(res);
					},
					err => {
						console.log(err);
					}
				)
		);
	}

	/**
	 * Destroys the component
	 */
	ngOnDestroy() {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}

	getIdentityLookupValues() {
		this.spinner.startLoader();
		this.subscriptions.push(
			forkJoin({
				getGenderIdentityLookup: this.lookupService.getGenderIdentityLookup(),
				getPreferredTitle: this.lookupService.getPreferredTitleLookup(),
				getPronouns: this.lookupService.getPreferredPronounsLookup(),
			}).subscribe(
				response => {
					this.genderIdentityLookup = response.getGenderIdentityLookup.data;
					this.preferredTitleLookup = response.getPreferredTitle.data;
					this.preferredPronounsLookup = response.getPronouns.data;
				},
				err => {
					console.error(`[${PersonalInfoComponent.name}][${this.getIdentityLookupValues.name}]`, 'error getting how you move data', err);
					this.spinner.stopLoader();
				}
			)
		);
	}
}
